import {Discount} from "crqlar-react-common/dist/models/billing/invoiceItem.model";
import {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";
import {useTranslation} from "react-i18next";
import {formatPercent} from "../../../../digitalSigning/public/format.util";

const TWO_DECIMALS = {minimumFractionDigits: 2, maximumFractionDigits: 2};

/**
 * Cents with the currency's code, never its sign - the folio dialog shows "EUR" everywhere (its lines
 * once, in the column headers): "EUR 1,234.50" (en), "1.234,50 EUR" (de). An unknown currency or
 * language still gets its amount.
 */
export const formatMoneyCode = (cents: number, currency: string, language: string): string => {
    try {
        return new Intl.NumberFormat(language, {style: 'currency', currency, currencyDisplay: 'code', ...TWO_DECIMALS}).format(cents / 100);
    } catch {
        let amount: string;
        try {
            amount = new Intl.NumberFormat(language, TWO_DECIMALS).format(cents / 100);
        } catch {
            amount = (cents / 100).toFixed(2);
        }
        return currency ? `${amount} ${currency}` : amount;
    }
};

/** Amount text as staff type it ("12,50", "12.5", "1.234,50") in cents; undefined when empty or not an amount. */
export const parseAmountText = (text: string): number | undefined => {
    const compact = text.replace(/\s/g, '');
    if (!compact) return undefined;

    // A comma is the decimal separator, dots before it group thousands
    const normalized = compact.includes(',') ? compact.replace(/\./g, '').replace(',', '.') : compact;
    if (!/^\d*\.?\d*$/.test(normalized) || normalized === '.') return undefined;
    return Math.round(parseFloat(normalized) * 100);
};

/** Cents as the amount field shows them: two decimals in the language's style, no grouping. */
export const amountText = (cents: number | undefined, language: string): string => {
    if (cents == null) return '';
    try {
        return new Intl.NumberFormat(language, {minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false}).format(cents / 100);
    } catch {
        return (cents / 100).toFixed(2);
    }
};

export interface FolioFormat {
    language: string
    /** The folio currency's code, "EUR" - the lines' Price and Total headers name it: "Price (EUR)". */
    currencyCode: string
    /** "EUR 12.50" (en), "12,50 EUR" (de). */
    money: (cents: number) => string
    /**
     * The number alone, "1234.50" (en), "1234,50" (de) - no grouping, like the price field: the lines'
     * Price and Total cells, whose headers name the code.
     */
    amount: (cents: number) => string
    /** "20 % incl."; undefined without a rate. */
    vat: (taxRate?: Pick<TaxRate, 'rate' | 'inclusive'>) => string | undefined
    /**
     * "−10 %", or an amount off without the code, "−5.00": it sits in the price cell, beside the
     * price, under the header that names the code. Undefined without a discount.
     */
    discount: (discount?: Discount) => string | undefined
}

/** Money, VAT and discounts in the folio's currency (as its code) and the staff's language. */
export const useFolioFormat = (currency: string): FolioFormat => {
    const {t, i18n} = useTranslation();
    const language = i18n.language || 'en';

    return {
        language,
        currencyCode: currency,
        money: cents => formatMoneyCode(cents, currency, language),
        amount: cents => amountText(cents, language),
        vat: taxRate => taxRate?.rate == null
            ? undefined
            : `${formatPercent(taxRate.rate, language, 2)} ${(taxRate.inclusive ?? true) ? t('incl.') : t('excl.')}`,
        discount: discount => {
            if (discount?.percentOff) return `−${formatPercent(discount.percentOff, language, 2)}`;
            if (discount?.amountCentsOff) return `−${amountText(discount.amountCentsOff, language)}`;
            return undefined;
        },
    };
};
